import React, { useEffect, useRef, useState } from 'react';
import { portalApi } from '../../lib/api';
import SafeIcon from '../../common/SafeIcon';
import { Icons } from '../icons';

// Photon (OSM) address autocomplete — hits the Node proxy
// (GET /api/geocode), never photon.komoot.io directly.
// 300ms debounce, ≥3 chars, UK bias via a Leamington Spa default centre.
// onSelect(place) receives the structured place:
//   { label, line1, line2, city, postcode, country, lat, lng }
const DEFAULT_BIAS = { lat: 52.287, lon: -1.536 }; // Leamington Spa — UK-first results

export default function AddressAutocomplete({ onSelect, placeholder = 'Start typing an address…', initial = '', bias = DEFAULT_BIAS }) {
  const [query, setQuery] = useState(initial);
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [highlight, setHighlight] = useState(-1);
  const timer = useRef(null);
  const seq = useRef(0);
  const rootRef = useRef(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    const q = query.trim();
    if (q.length < 3) {
      setResults([]);
      setOpen(false);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    timer.current = setTimeout(async () => {
      const requestId = ++seq.current;
      try {
        const res = await portalApi.geocode(q, bias.lat, bias.lon);
        if (seq.current !== requestId) return; // stale response — a newer keystroke won
        setResults(res?.results || []);
        setError('');
        setOpen(true);
      } catch (err) {
        if (seq.current !== requestId) return;
        setResults([]);
        setError(err.message || 'Address search failed');
        setOpen(false);
      } finally {
        if (seq.current === requestId) setLoading(false);
      }
    }, 300);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [query, bias.lat, bias.lon]);

  // Click-outside closes the dropdown.
  useEffect(() => {
    const handler = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const pick = (place) => {
    setQuery(place.label);
    setOpen(false);
    setHighlight(-1);
    onSelect?.(place);
  };

  const onKeyDown = (event) => {
    if (!open || !results.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlight((i) => (i + 1) % results.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlight((i) => (i - 1 + results.length) % results.length);
    } else if (event.key === 'Enter' && highlight >= 0) {
      event.preventDefault();
      pick(results[highlight]);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className="address-autocomplete" ref={rootRef}>
      <label className="search-input">
        <SafeIcon icon={Icons.Search} />
        <input
          type="text"
          value={query}
          placeholder={placeholder}
          onChange={(event) => setQuery(event.target.value)}
          onFocus={() => results.length && setOpen(true)}
          onKeyDown={onKeyDown}
          autoComplete="off"
        />
        {loading && <span className="autocomplete-spinner" aria-label="Searching" />}
      </label>
      {error && <p className="field-hint error-text">{error}</p>}
      {open && results.length > 0 && (
        <ul className="autocomplete-list" role="listbox">
          {results.map((place, index) => (
            <li
              key={`${place.label}-${index}`}
              role="option"
              aria-selected={index === highlight}
              className={index === highlight ? 'highlighted' : ''}
              onMouseDown={(event) => { event.preventDefault(); pick(place); }}
              onMouseEnter={() => setHighlight(index)}
            >
              <span>{place.label}</span>
              <small>{[place.city, place.postcode, place.country].filter(Boolean).join(' · ')}</small>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
